import { ChevronRight, Loader2 } from "lucide-react";

export interface ServiceCardProps {
  name: string;
  description: string | null;
  bgImageUrl: string | null;
  isLoading?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  interactive?: boolean;
}

export function ServiceCard({
  name,
  description,
  bgImageUrl,
  isLoading = false,
  onClick,
  disabled = false,
  interactive = true,
}: ServiceCardProps) {
  const displayDescription = description || "Service personnalisé";

  const content = (
    <>
      {/* Background image if exists */}
      {bgImageUrl && (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgImageUrl})` }} />
      )}
      {/* Overlay for readability - full opacity only when no image */}
      <div className={`absolute inset-0 ${bgImageUrl ? "bg-primary/60" : "bg-primary"}`} />

      {/* Content */}
      <div className="relative z-10 flex-1 min-w-0">
        <h4 className="font-serif font-medium text-white text-xl sm:text-2xl mb-1">{name}</h4>
        <p className="text-base sm:text-lg text-white/80 line-clamp-2">{displayDescription}</p>
      </div>
      {isLoading ? (
        <Loader2 size={20} className="relative z-10 text-white/70 shrink-0 animate-spin" />
      ) : (
        <>
          <ChevronRight size={20} className="relative z-10 sm:hidden text-white/70 shrink-0" />
          <ChevronRight size={24} className="relative z-10 hidden sm:block text-white/70 shrink-0" />
        </>
      )}
    </>
  );

  const baseClasses =
    "w-full relative flex items-center gap-4 sm:gap-5 p-4 sm:p-5 rounded-xl border border-border overflow-hidden min-h-[100px]";
  const interactiveClasses = interactive
    ? "hover:border-primary/30 hover:shadow-gold transition-all duration-300 touch-feedback"
    : "";
  const disabledClasses = disabled ? "opacity-70 cursor-not-allowed" : "";

  if (interactive && onClick) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`${baseClasses} ${interactiveClasses} ${disabledClasses} text-left`}
      >
        {content}
      </button>
    );
  }

  return <div className={`${baseClasses} ${interactiveClasses} ${disabledClasses}`}>{content}</div>;
}
